import { RootState } from 'types';
import { createSelector } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { parseActionDAO } from './action';
import { IEntityReducer } from 'core/reducers/entities';
import { ActionEntityType, ActionType, IActionDAO, IActionNode } from 'types/domain/action';
import { Handles } from 'constants/handles';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { INode } from 'types/domain/base';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.ACTION];
const offlineId = selectOfflineId;

export const selectActionPlaceholders = createSelector(
  entity,
  id,
  offlineId,
  (entity: IEntityReducer, id, offlineId) => {
    const action: IActionDAO = parseActionDAO(entity, id);
    return parseActionPlaceholders(action, action.node, offlineId);
  },
);

export function parseActionPlaceholders(action: IActionDAO, position: any, index) {
  const placeholders = [];

  const width = 250;
  const height = 50;

  function placeholder(attribs?) {
    index--;
    return {
      id: `${index}`,
      offlineId: index,
      target: index.toString(),
      type: EntityTypes.ACTION,
      position: {
        x: position.x + attribs.position.x,
        y: position.y + attribs.position.y,
      },
      data: { id: index, type: ActionType.FUTURE, ...(attribs || {}) },
    };
  }

  if (!action.childId && !action.outs[Handles.DEFAULT])
    placeholders.push(
      placeholder({
        source: action.id.toString(),
        sourceHandle: Handles.DEFAULT,
        sourceId: action.id,
        parentActionId: action.id,
        position: { x: width, y: 0 },
      }),
    );
  if (
    !action.outs[Handles.FAIL] &&
    action.entities.some(ent => !ent.correct) &&
    action.entities.some(ent => ent.correct)
  ) {
    placeholders.push(
      placeholder({
        source: action.id.toString(),
        sourceHandle: Handles.FAIL,
        parentActionId: action.id,
        text: 'incorrect',
        position: { x: width, y: -height },
      }),
    );
  }

  if (action.timeLimit && !action.outs[Handles.TIME])
    placeholders.push(
      placeholder({
        source: action.id.toString(),
        sourceHandle: Handles.TIME,
        parentActionId: action.id,
        text: 'time expired',
        position: { x: width, y: -height * 2 },
      }),
    );

  const entities = action.entities
    .filter(ent =>
      ent.status > -1 && action.type === ActionType.INTERACTIVE
        ? ![ActionEntityType.BASIC, ActionEntityType.TEXT].includes(ent.type)
        : [ActionEntityType.BASIC].includes(ent.type),
    )
    .filter(ent => !action.outs[ent.id])
    .reverse();

  entities.map((ent, i) =>
    placeholders.push(
      placeholder({
        source: action.id.toString(),
        sourceHandle: `${ent.id}`,
        entityId: ent.id,
        parentEntityId: ent.id,
        parentActionId: action.id,
        name: ent.text?.shortText || ent.name,
        position: { x: width, y: height * (2 + entities.length - i) },
      }),
    ),
  );

  return placeholders;
}
